import React, { useState, useEffect, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { Card, Button, Form, Alert, Spinner, Badge } from 'react-bootstrap'
import { chatsAPI } from '../../services/chatsApi'
import { stageTasksAPI } from '../../services/api'
import ChatSectionHeader from './ChatSectionHeader'

const formatDate = (timestamp) =>
  timestamp ? new Date(timestamp).toLocaleDateString('ru-RU') : ''

const ChatStageTasks = () => {
  const { id, stageId } = useParams()
  const [stage, setStage] = useState(null)
  const [tasks, setTasks] = useState([])
  const [newTitle, setNewTitle] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const loadStage = useCallback(async () => {
    try {
      setLoading(true)
      const response = await chatsAPI.stage(id, stageId)
      setStage(response.data)
      setTasks(response.data.tasks)
      setError(null)
    } catch (err) {
      console.error('Error loading stage tasks:', err)
      setError(err.response?.status === 403 ? 'У вас нет доступа к этому чату' : 'Не удалось загрузить задачи')
    } finally {
      setLoading(false)
    }
  }, [id, stageId])

  useEffect(() => {
    loadStage()
  }, [loadStage])

  const handleCreate = async (e) => {
    e.preventDefault()
    const title = newTitle.trim()
    if (!title || !stage) return

    try {
      setSaving(true)
      const response = await stageTasksAPI.create(stage.project_id, stage.id, title)
      setTasks((prev) => [...prev, response.data])
      setNewTitle('')
    } catch (err) {
      console.error('Error creating task:', err)
      setError('Не удалось добавить задачу')
    } finally {
      setSaving(false)
    }
  }

  const handleToggle = async (task) => {
    try {
      const response = await stageTasksAPI.toggleCompletion(stage.project_id, stage.id, task.id)
      setTasks((prev) =>
        prev.map((t) => (t.id === task.id ? { ...t, completed: response.data.completed } : t))
      )
    } catch (err) {
      console.error('Error toggling task:', err)
      setError('Не удалось изменить статус задачи')
    }
  }

  const handleDelete = async (task) => {
    if (!window.confirm('Удалить задачу?')) return

    try {
      await stageTasksAPI.delete(stage.project_id, stage.id, task.id)
      setTasks((prev) => prev.filter((t) => t.id !== task.id))
    } catch (err) {
      console.error('Error deleting task:', err)
      setError('Не удалось удалить задачу')
    }
  }

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
      </div>
    )
  }

  if (error && !stage) {
    return <Alert variant="danger">{error}</Alert>
  }

  return (
    <div>
      <ChatSectionHeader
        title={`Этап ${stage.number}. Задачи`}
        subtitle={stage.name}
        backTo={`/chats/${id}/stages/${stageId}`}
      />

      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

      <Form onSubmit={handleCreate} className="d-flex gap-2 mb-3">
        <Form.Control
          type="text"
          placeholder="Новая задача"
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
        />
        <Button type="submit" variant="primary" disabled={saving || !newTitle.trim()}>
          Добавить
        </Button>
      </Form>

      {tasks.length === 0 ? (
        <Alert variant="light" className="border text-center text-muted">
          Задач для этого этапа пока нет
        </Alert>
      ) : (
        <Card className="shadow-sm border-0">
          <div className="list-group list-group-flush">
            {tasks.map((task, index) => (
              <div key={task.id} className="list-group-item py-3">
                <div className="d-flex justify-content-between align-items-start gap-2">
                  <div className="flex-grow-1" style={{ minWidth: 0 }}>
                    <div className={`fw-semibold ${task.completed ? 'text-decoration-line-through text-muted' : ''}`}>
                      {index + 1}. {task.title}
                    </div>
                    <div className="text-muted small">{formatDate(task.created_at)}</div>
                  </div>
                  <div className="d-flex align-items-center gap-2 flex-shrink-0">
                    <Badge
                      bg={task.completed ? 'success' : 'warning'}
                      text={task.completed ? undefined : 'dark'}
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleToggle(task)}
                      title="Нажмите, чтобы изменить статус"
                    >
                      {task.completed ? 'Готово' : 'В работе'}
                    </Badge>
                    <Button variant="outline-danger" size="sm" onClick={() => handleDelete(task)}>
                      <i className="bi bi-trash"></i>
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  )
}

export default ChatStageTasks
